import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 *
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  if (!productsData) {
    return;
  }

  const cartItems = [];
  cartData.forEach((cartItem) => {
    const product = productsData.find((p) => p._id === cartItem.productId);
    if (product) {
      cartItems.push({
        name: product.name,
        category: product.category,
        cost: product.cost,
        rating: product.rating,
        image: product.image,
        _id: product._id,
        qty: cartItem.qty,
        totalCost: cartItem.qty * product.cost,
      });
    }
  });
  return cartItems;
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  let sum = 0;
  if (items) {
    items.forEach((id) => {
      sum = sum + id.qty * id.cost;
    });
    return sum;
  }
  return 0;
};

export const getTotalItems = (items = []) => {
  let qty = 0;
  items.forEach((ele) => {
    qty = qty + ele.qty;
  });
  return qty;
};

/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 *
 */
const ItemQuantity = ({ productId, value, handleAdd, handleDelete }) => {
  // const [stateValue, setStateValue] = useState(value);
  return (
    <Stack direction="row" alignItems="center">
      <IconButton
        size="small"
        color="primary"
        onClick={(e) => {
          handleDelete(productId);
        }}
      >
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton
        size="small"
        color="primary"
        onClick={(e) => {
          handleAdd(productId);
        }}
      >
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 *
 */
const Cart = ({ products, items = [], handleQuantity, isReadOnly }) => {
  let history = useHistory();

  const handleAdd = (productId) => {
    let Qty;
    items.forEach((ele) => {
      if (ele._id === productId) {
        console.log(ele.qty);
        Qty = ele.qty;
      }
    });
    console.log(productId);
    handleQuantity(productId, Qty + 1);
  };

  const handleDelete = (productId) => {
    // if(stateValue>0){
    //   setStateValue(stateValue - 1);
    // }
    let Qty;
    items.forEach((ele) => {
      if (ele._id === productId) {
        console.log(ele.qty);
        Qty = ele.qty;
      }
    });
    console.log(productId);
    if (Qty > 0) {
      handleQuantity(productId, Qty - 1);
    } else {
      handleQuantity(productId, 0);
    }
  };

  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className="cart">
        {items.map((mp) => (
          <Box
            key={mp._id}
            display="flex"
            alignItems="flex-start"
            padding="1rem"
          >
            <Box className="image-container">
              <img src={mp.image} alt={mp.name} width="100%" height="100%" />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              height="6rem"
              paddingX="1rem"
            >
              <div>{mp.name}</div>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {isReadOnly ? (
                  <Box>Qty:{mp.qty}</Box>
                ) : (
                  <ItemQuantity
                    key={mp._id}
                    productId={mp._id}
                    value={mp.qty}
                    handleAdd={handleAdd}
                    handleDelete={handleDelete}
                  />
                )}

                <Box padding="0.5rem" fontWeight="700">
                  ${mp.cost}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}

        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" className="cart-footer">
          {
            // console.log(window.location.pathname)
            isReadOnly ? (
              <></>
            ) : (
              <Button
                color="primary"
                variant="contained"
                onClick={(e) => {
                  history.push("/checkout");
                }}
                className="checkout-btn"
              >
                Checkout
              </Button>
            )
          }
        </Box>
      </Box>
      {isReadOnly ? (
        <Box className="cart" padding={2}>
          <h2>Order Details</h2>
          <table>
            <tr>
              <td>Products</td>
              <td>{getTotalItems(items)}</td>
            </tr>
            <tr>
              <td>SubTotal</td>
              <td>${getTotalCartValue(items)}</td>
            </tr>
            <tr>
              <td>Shipping Charges</td>
              <td>$0</td>
            </tr>
            <tr>
              <td>
                <h3>Total</h3>
              </td>
              <td>
                <h3>${getTotalCartValue(items)}</h3>
              </td>
            </tr>
          </table>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};

export default Cart;
